import type { ExportBytesOptions, ExportBytesResult, OpenedFile, PickOpenFileOptions } from "./types";

export function triggerBrowserDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = window.document.createElement("a");
  link.href = url;
  link.download = filename;
  link.rel = "noopener";
  window.document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export async function browserExportBytes(
  bytes: Uint8Array,
  options: ExportBytesOptions,
): Promise<ExportBytesResult> {
  const blob = new Blob([new Uint8Array(bytes)], { type: options.mimeType });
  triggerBrowserDownload(blob, options.filename);
  return { method: "download" };
}

export async function browserPickOpenFile(
  options: PickOpenFileOptions = {},
): Promise<OpenedFile | undefined> {
  const files = await browserPickOpenFiles({ ...options, multiple: false });
  return files[0];
}

export async function browserPickOpenFiles(
  options: PickOpenFileOptions = {},
): Promise<OpenedFile[]> {
  if (typeof window === "undefined" || typeof window.document === "undefined") {
    return [];
  }

  return await new Promise(resolve => {
    const input = window.document.createElement("input");
    input.type = "file";
    input.hidden = true;
    if (options.accept) {
      input.accept = options.accept;
    }
    if (options.multiple) {
      input.multiple = true;
    }

    let settled = false;
    const finish = (files: OpenedFile[]) => {
      if (settled) {
        return;
      }
      settled = true;
      input.remove();
      window.removeEventListener("focus", onWindowFocus);
      resolve(files);
    };

    const onWindowFocus = () => {
      // If the user cancels the dialog, change may never fire; settle on next focus tick.
      window.setTimeout(() => {
        if (!settled && (!input.files || input.files.length === 0)) {
          finish([]);
        }
      }, 300);
    };

    input.addEventListener("change", () => {
      void (async () => {
        const list = input.files;
        if (!list || list.length === 0) {
          finish([]);
          return;
        }
        const opened: OpenedFile[] = [];
        for (const file of Array.from(list)) {
          opened.push({
            name: file.name,
            bytes: new Uint8Array(await file.arrayBuffer()),
          });
        }
        finish(opened);
      })();
    });

    window.document.body.append(input);
    window.addEventListener("focus", onWindowFocus);
    input.click();
  });
}

export function browserSetWindowTitle(title: string): void {
  if (typeof window.document !== "undefined") {
    window.document.title = title;
  }
}

export function browserConfirmDiscard(message: string): boolean {
  if (typeof window === "undefined") {
    return true;
  }
  return window.confirm(message);
}
